// services/MetaPixelService.ts

declare global {
    interface Window {
        fbq: (
            method: string,
            eventName: string,
            params?: Record<string, any>,
            options?: { pixelId: string }
        ) => void;
        _fbq: any;
        metaPixelIds: string[];
    }
}

export interface AdvancedMatchingParams {
    em?: string; // Email
    ph?: string; // Phone number
    fn?: string; // First name
    ln?: string; // Last name
    db?: string; // Date of birth
    ge?: string; // Gender
    ct?: string; // City
    st?: string; // State/Province
    zp?: string; // Zip/Postal code
    country?: string; // Country
    external_id?: string; // External ID
}

class MetaPixelService {
    private initializedPixels: string[];

    constructor() {
        // Initialize with any pixels that were already set up in index.html
        this.initializedPixels = window.metaPixelIds || [];
    }

    public getInitializedPixels(): string[] {
        return [...this.initializedPixels];
    }

    public isPixelInitialized(pixelId: string): boolean {
        return this.initializedPixels.includes(pixelId);
    }

    public initializePixel(
        pixelId: string,
        advancedMatching: AdvancedMatchingParams = {}
    ): boolean {
        if (!pixelId || this.initializedPixels.includes(pixelId)) {
            return false;
        }

        try {
            window.fbq('init', pixelId, advancedMatching);

            this.initializedPixels.push(pixelId);
            window.metaPixelIds = this.initializedPixels;
            return true;
        } catch (error) {
            console.error(`Error initializing pixel ${pixelId}:`, error);
            return false;
        }
    }

    public trackEvent(eventName: string, params: Record<string, any> = {}): void {
        if (this.initializedPixels.length === 0) return;

        try {
            window.fbq('track', eventName, params);
        } catch (error) {
            console.error(`Error tracking event ${eventName}:`, error);
        }
    }

    public trackEventOnPixel(
        pixelId: string,
        eventName: string,
        params: Record<string, any> = {}
    ): void {
        if (!this.initializedPixels.includes(pixelId)) return;

        try {
            window.fbq('track', eventName, params, { pixelId });
        } catch (error) {
            console.error(`Error tracking event ${eventName} on pixel ${pixelId}:`, error);
        }
    }

    public trackCustomEvent(eventName: string, params: Record<string, any> = {}): void {
        if (this.initializedPixels.length === 0) return;

        try {
            window.fbq('trackCustom', eventName, params);
        } catch (error) {
            console.error(`Error tracking custom event ${eventName}:`, error);
        }
    }

    public trackCustomEventOnPixel(
        pixelId: string,
        eventName: string,
        params: Record<string, any> = {}
    ): void {
        if (!this.initializedPixels.includes(pixelId)) return;

        try {
            window.fbq('trackCustom', eventName, params, { pixelId });
        } catch (error) {
            console.error(`Error tracking custom event ${eventName} on pixel ${pixelId}:`, error);
        }
    }
}

// Export a singleton instance
export const metaPixelService = new MetaPixelService();
export default metaPixelService;
